import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  HomeIcon,
  MicrophoneIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  DocumentIcon,
  CameraIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowLeftOnRectangleIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { isSubscribed } = useSubscription();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'All Notes', href: '/notes', icon: DocumentTextIcon },
    { name: 'Record Audio', href: '/record', icon: MicrophoneIcon },
    { name: 'YouTube', href: '/youtube', icon: VideoCameraIcon },
    { name: 'PDF Processor', href: '/pdf-processor', icon: DocumentIcon },
    { name: 'Tutor', href: '/chat-tutor', icon: ChatBubbleLeftRightIcon },
    { name: 'Homework Solver', href: '/photo-tutor', icon: CameraIcon },
    { name: 'Account', href: '/account', icon: UserIcon },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleUpgrade = () => {
    setSidebarOpen(false);
    navigate('/paywall');
  };

  return (
    <div className="h-screen flex bg-black overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex h-full flex-col bg-black border-r border-gray-800">
          {/* Header */}
          <div className="flex h-20 items-center px-6 border-b border-gray-800">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-800 border border-gray-700 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">L</span>
              </div>
              <span className="text-2xl font-bold text-white tracking-tight">Lecha</span>
            </div>
            
            {/* Mobile close button */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden ml-auto p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* User info */}
          <div className="px-6 py-6 border-b border-gray-800">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gray-800 border border-gray-700 rounded-xl flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-white truncate">
                  {currentUser?.email?.split('@')[0] || currentUser?.displayName || 'User'}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${isSubscribed ? 'bg-purple-500' : 'bg-green-500'}`}></div>
                  <p className="text-sm text-gray-400">{isSubscribed ? 'Lecha Pro' : 'Free Plan'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive 
                      ? 'bg-gray-800 text-white border border-gray-700' 
                      : 'text-gray-400 hover:text-white hover:bg-gray-900'
                  }`}
                >
                  <item.icon className={`mr-4 h-5 w-5 transition-colors ${
                    isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'
                  }`} />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-800 space-y-2">
            {!isSubscribed && (
              <button
                onClick={handleUpgrade}
                className="w-full flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl transition-all duration-200 shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 group"
              >
                <SparklesIcon className="mr-2 h-5 w-5" />
                <span>Upgrade to Premium</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-900 rounded-xl transition-all duration-200 group"
            >
              <ArrowLeftOnRectangleIcon className="mr-4 h-5 w-5 text-gray-500 group-hover:text-gray-300 transition-colors" />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-auto">
        {/* Top bar */}
        <div className="bg-black border-b border-gray-800">
          <div className="flex h-16 items-center px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors mr-4"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-white">
                {getCurrentPageTitle(location.pathname)}
              </h1>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-black">
          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
            <div className="animate-fadeInUp">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const getCurrentPageTitle = (pathname) => {
  const titles = {
    '/': 'Dashboard',
    '/record': 'Record Audio',
    '/notes': 'All Notes',
    '/youtube': 'YouTube Processor',
    '/pdf-processor': 'PDF Processor',
    '/chat-tutor': 'Tutor',
    '/photo-tutor': 'Homework Solver',
    '/account': 'Account Settings',
    '/quiz-setup': 'Quiz Setup',
    '/quiz': 'Quiz'
  };
  
  // Handle dynamic routes
  if (pathname.startsWith('/recording/')) return 'Recording Details';
  if (pathname.startsWith('/pdf/')) return 'PDF Details';
  if (pathname.startsWith('/youtube/')) return 'YouTube Details';
  
  return titles[pathname] || 'Lecha';
};

export default Layout;
